import { useCallback, useEffect, useMemo, useState } from 'react';
import communicationsService from '../services/communicationsService.js';
import {
  detectAlignmentForThread,
  filterFeedItems,
  findBestThreadKeyForQuery,
  getThreadPreviewBySource,
} from '../services/communications/communicationsPresenters.js';
import { isThreadResolved } from '../services/communications/communicationsMockAdapter.js';
import { ALIGNMENT_STATUS } from '../services/communications/communicationsTypes.js';

export function useCommunicationsDashboard(projectId, projectMembers = []) {
  const [connections, setConnections] = useState(null);
  const [feedItems, setFeedItems] = useState([]);
  const [productivity, setProductivity] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeThreadKey, setActiveThreadKey] = useState(null);
  const [filters, setFilters] = useState({
    sources: [],
    assignee: '',
    dateRange: '',
    query: '',
    threadKey: '',
    alignment: '',
  });
  const [alignmentAnswer, setAlignmentAnswer] = useState(null);
  const [alignmentLoading, setAlignmentLoading] = useState(false);

  const isPreviewMode = communicationsService.isCommunicationsPreviewMode();

  const loadAll = useCallback(async () => {
    if (!projectId) {
      setConnections(null);
      setFeedItems([]);
      setProductivity(null);
      setIssues([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [conn, feed, prod, issueList] = await Promise.all([
        communicationsService.getProjectConnections(projectId),
        communicationsService.getCommunicationsFeed(projectId),
        communicationsService.getProductivitySummary(projectId),
        communicationsService.getCommunicationsIssues(projectId),
      ]);
      setConnections(conn);
      setFeedItems(feed?.items || []);
      setProductivity(prod);
      setIssues(issueList || []);
    } catch (err) {
      setError(err?.message || 'Failed to load communications');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredItems = useMemo(
    () => filterFeedItems(feedItems, filters, feedItems),
    [feedItems, filters]
  );

  useEffect(() => {
    if (filters.query) {
      const key = findBestThreadKeyForQuery(feedItems, filters.query);
      if (key) setActiveThreadKey(key);
    }
  }, [filters.query, feedItems]);

  useEffect(() => {
    if (filters.threadKey) {
      setActiveThreadKey(filters.threadKey);
    }
  }, [filters.threadKey]);

  const activeConflict = useMemo(() => {
    if (!activeThreadKey) return null;
    const conflict = detectAlignmentForThread(feedItems, activeThreadKey);
    if (!conflict) return null;
    if (isThreadResolved(activeThreadKey)) {
      return { ...conflict, status: ALIGNMENT_STATUS.ALIGNED };
    }
    return conflict;
  }, [activeThreadKey, feedItems]);

  const threadPreviewBySource = useMemo(() => {
    if (!activeThreadKey) return { outlook: null, teams: null, planner: null };
    return getThreadPreviewBySource(feedItems, activeThreadKey);
  }, [activeThreadKey, feedItems]);

  const configureCommunications = useCallback(
    async (config) => {
      const result = await communicationsService.configureCommunications(projectId, config);
      setConnections(result);
      return result;
    },
    [projectId]
  );

  const refreshFeed = useCallback(async () => {
    const feed = await communicationsService.refreshFeed(projectId);
    setFeedItems(feed?.items || []);
  }, [projectId]);

  const askAlignmentQuestion = useCallback(
    async (question) => {
      if (!activeThreadKey) return null;
      setAlignmentLoading(true);
      try {
        const result = await communicationsService.askAlignmentQuestion(projectId, {
          threadKey: activeThreadKey,
          question,
        });
        setAlignmentAnswer(result);
        return result;
      } finally {
        setAlignmentLoading(false);
      }
    },
    [projectId, activeThreadKey]
  );

  const recordAlignedDecision = useCallback(
    async (decisionText) => {
      if (!activeThreadKey) return null;
      await communicationsService.recordAlignedDecision(projectId, {
        threadKey: activeThreadKey,
        decisionText,
      });
      setAlignmentAnswer(null);
      await loadAll();
      return true;
    },
    [projectId, activeThreadKey, loadAll]
  );

  const members = useMemo(() => {
    if (projectMembers?.length) {
      return projectMembers.map((m) => ({
        id: m.id || m.userId,
        name: m.user?.name || m.name || m.email || 'Member',
        email: m.user?.email || m.email,
        role: m.role,
      }));
    }
    return (productivity?.teamRows || []).map((r) => ({
      id: r.id,
      name: r.name,
      role: r.role,
    }));
  }, [projectMembers, productivity]);

  return {
    connections,
    feedItems,
    productivity,
    issues,
    members,
    loading,
    error,
    isPreviewMode,
    filters,
    setFilters,
    filteredItems,
    activeThreadKey,
    setActiveThreadKey,
    activeConflict,
    threadPreviewBySource,
    configureCommunications,
    refreshFeed,
    askAlignmentQuestion,
    recordAlignedDecision,
    alignmentAnswer,
    alignmentLoading,
    setAlignmentAnswer,
    reload: loadAll,
  };
}

export default useCommunicationsDashboard;
